import { Link } from 'react-router-dom';
import { Icon } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import { useSetupStatus } from '../onboarding/index.js';
import type { SetupStatus } from '../onboarding/index.js';
import { useI18n } from '../../i18n/index.js';

/**
 * The completeness mark beside a business name.
 *
 * Only ever a WARNING here: a green tick would sit beside the business name on
 * every page of the app, permanently, saying a thing that is true of most
 * businesses most of the time — a reassurance nobody asked for, occupying the
 * most valuable space in the UI. The warning stays, because that one is news.
 *
 * While the checklist's data is still loading the answer is unknown, so it
 * renders nothing rather than holding a gap open for an icon that usually never
 * arrives. Flashing a warning on every page load — including for businesses
 * that turn out to be complete — is how an indicator teaches people to stop
 * reading it.
 */
function SetupMark({ status }: { status: SetupStatus }) {
  const { t } = useI18n();
  if (!status.loaded || status.complete) return null;
  return (
    <span className="setup-mark setup-mark--todo">
      <Icon
        name="alert-circle"
        size={18}
        title={t('businesses.setupIncompleteCount', { count: status.missing.length })}
      />
    </span>
  );
}

/**
 * Which business you are in, in the top bar. Not a switcher any more.
 *
 * It used to open a dropdown that switched businesses in place. The name sits
 * in the header of every page, so the chevron was an invitation on every screen
 * to do the one thing that changes what everything else on that screen means —
 * and accounts with a single business, which is nearly all of them, got a menu
 * with exactly one row in it. Switching lives in the account menu's "My
 * businesses", one deliberate step away, where the list is the page's subject
 * rather than a surprise under the title.
 *
 * With `linkHome` the name goes to the business's dashboard — the header title
 * taking you to the app's front page is what people try first. The setup
 * wizard's bar leaves it a plain label: the dashboard is locked until setup is
 * finished, and a link there would only bounce the owner back into the wizard.
 *
 * `setupMark={false}` drops the incomplete-setup warning. The setup wizard's
 * bar passes it: the whole page under it IS the unfinished setup, so a warning
 * beside the name only repeats the page as an alarm.
 *
 * Kept as its own component (and its own name) because both shells mount it:
 * the app shell and the setup wizard's bar.
 */
export function BusinessSwitcher({ linkHome = false, setupMark = true }: { linkHome?: boolean; setupMark?: boolean }) {
  const { t } = useI18n();
  const active = useActiveBusiness();
  const status = useSetupStatus(active);

  const content = (
    <>
      {setupMark && <SetupMark status={status} />}
      {/* Name only. The number that used to sit under it was ours, not theirs,
          in the pooled case (doc 18 §7.4) — printed under a business name it
          reads as "this is your new business number", which is the one thing we
          never want an owner to believe. Their own line is on Settings ›
          Number, in the context that explains it. */}
      <span className="biz-label__name">{active.name}</span>
    </>
  );

  return linkHome ? (
    <Link
      to={`/b/${active.id}`}
      className="biz-label biz-label--link"
      title={active.name}
      aria-label={`${active.name}, ${t('nav.dashboard')}`}
    >
      {content}
    </Link>
  ) : (
    <span className="biz-label" title={active.name}>
      {content}
    </span>
  );
}
